import { Fragment, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouteObject } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { CookieConsent, IdleTimeoutHandler, Page403, Page404, Page500 } from '@zeta-commons';
import { axiosInstance, enqueueSnackbar } from '@zeta-core';
import { ErrorStatusCodes, getErrorMessage, ServerErrorMessage } from '@zeta-services';
import CompactLayout from '../../layouts/compact-layout';
import AuthRoutes from './auth-routes';
import DashboardRoutes from './dashboard-routes';
import RegistrationRoutes from './register-routes';

// ----------------------------------------------------------------------

interface IRoute {
  path?: string;
  index?: boolean;
  element?: ReactNode;
  children?: IRoute[];
}

export default function Router() {
  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.response.use(
      (response) => {
        if (ErrorStatusCodes?.includes(response.status)) {
          enqueueSnackbar(getErrorMessage({ response }, undefined, ServerErrorMessage), {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          });
        }

        return response;
      },
      (error) => {
        if (ErrorStatusCodes?.includes(error?.response?.status)) {
          enqueueSnackbar(getErrorMessage(error, undefined, ServerErrorMessage), {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          });
        }

        return Promise.reject(error);
      }
    );

    return () => axiosInstance.interceptors.response.eject(interceptorId);
  }, []);

  const routes = useMemo(
    (): RouteObject[] => [
      {
        path: '/',
        element: <Navigate to="/auth/login" replace />,
      },
      ...AuthRoutes,
      ...RegistrationRoutes,
      ...DashboardRoutes,
      {
        path: '500',
        errorElement: <Page500 />,
        element: (
          <CompactLayout>
            <Page500 />
          </CompactLayout>
        ),
      },
      {
        path: '403',
        errorElement: <Page500 />,
        element: (
          <CompactLayout>
            <Page403 />
          </CompactLayout>
        ),
      },
      {
        path: '404',
        errorElement: <Page500 />,
        element: (
          <CompactLayout>
            <Page404 />
          </CompactLayout>
        ),
      },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
    []
  );

  const convertRoutes = useCallback((rs?: IRoute[], parentKey = 'root') => {
    return rs?.map((route, index) => {
      const routeKey = route.index ? `${parentKey}-index-${index}` : `${parentKey}-${route.path ?? 'pathless'}-${index}`;

      return (
        <Fragment key={routeKey}>
          <Route {...route}>{convertRoutes(route?.children, routeKey)}</Route>
        </Fragment>
      );
    });
  }, []);
  const componentRoutes = useMemo(() => convertRoutes(routes), [convertRoutes, routes]);

  const router = createBrowserRouter(createRoutesFromElements(componentRoutes));
  return (
    <>
      <IdleTimeoutHandler />
      <RouterProvider router={router} />
      <CookieConsent />
    </>
  );
}
